/**
 * Inbound Routing API
 *
 * Handles all inbound routing rule-related API calls.
 * Inbound routing determines which PROJECT incoming records are routed to
 * based on CRM entity data (evaluated globally within an organization).
 *
 * KEY DIFFERENCE from Delivery Rules:
 * - Inbound Routing: Determines TARGET PROJECT (evaluated globally within org)
 * - Delivery Rules: Determines CRM fields to update AFTER analysis
 */

import {
  INBOUND_ROUTING_PRIORITY_TAKEN,
  INBOUND_ROUTING_REORDER_CONFLICT,
  inboundRoutingFallbackResponseSchema,
  inboundRoutingListResponseSchema,
} from '@aica/api-types/schemas';
import { BaseApiClient, type ExpectedRefusal } from '../base';
import { API_ENDPOINTS } from '../endpoints';
import type {
  InboundRoutingRule,
  InboundRoutingListResponse,
  GetInboundRoutingRuleResponse,
  InboundRoutingFallbackSettings,
  InboundRoutingFallbackResponse,
  InboundRoutingReorderResponse,
  CreateInboundRoutingRuleRequest,
  UpdateInboundRoutingRuleRequest,
  UpdateInboundRoutingFallbackRequest,
  ReorderInboundRoutingRulesRequest,
} from '../types';

/** A manual priority another rule holds (#2949) — the rule form answers it at the field. */
const PRIORITY_TAKEN_REFUSALS: readonly ExpectedRefusal[] = [
  { status: 409, code: INBOUND_ROUTING_PRIORITY_TAKEN },
];

/** Rules changed under a reorder (#2874) — nothing saved, the screen says so. */
const REORDER_CONFLICT_REFUSALS: readonly ExpectedRefusal[] = [
  { status: 409, code: INBOUND_ROUTING_REORDER_CONFLICT },
];

export class InboundRoutingApi extends BaseApiClient {
  /**
   * Get inbound routing rules list for organization
   * Optionally filter by projectId
   */
  async getInboundRoutingRules(params?: {
    projectId?: string;
    limit?: number;
    offset?: number;
  }): Promise<InboundRoutingListResponse> {
    const query = this.buildQuery(params);
    // Validated: the routing status reads `meta.enabledTotal` and `isEnabled`
    // straight off this response. The schema checks those and lets the rest of
    // each rule through, so the cast only restores the rule's own UI type.
    const response = await this.validatedRequest(
      `${API_ENDPOINTS.INBOUND_ROUTING}${query}`,
      inboundRoutingListResponseSchema
    );
    return response as unknown as InboundRoutingListResponse;
  }

  /**
   * Get single inbound routing rule by ID
   */
  async getInboundRoutingRuleById(id: string): Promise<InboundRoutingRule> {
    const response = await this.request<GetInboundRoutingRuleResponse>(
      API_ENDPOINTS.INBOUND_ROUTING_BY_ID(id)
    );
    return response.data;
  }

  /**
   * Create new inbound routing rule
   */
  async createInboundRoutingRule(
    data: CreateInboundRoutingRuleRequest
  ): Promise<InboundRoutingRule> {
    const response = await this.request<{ data: InboundRoutingRule }>(
      API_ENDPOINTS.INBOUND_ROUTING,
      {
        method: 'POST',
        body: JSON.stringify(data),
        expectedRefusals: PRIORITY_TAKEN_REFUSALS,
      }
    );
    return response.data;
  }

  /**
   * Update inbound routing rule
   */
  async updateInboundRoutingRule(
    id: string,
    data: UpdateInboundRoutingRuleRequest
  ): Promise<InboundRoutingRule> {
    const response = await this.request<{ data: InboundRoutingRule }>(
      API_ENDPOINTS.INBOUND_ROUTING_BY_ID(id),
      {
        method: 'PUT',
        body: JSON.stringify(data),
        expectedRefusals: PRIORITY_TAKEN_REFUSALS,
      }
    );
    return response.data;
  }

  /**
   * Delete inbound routing rule
   */
  async deleteInboundRoutingRule(id: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(API_ENDPOINTS.INBOUND_ROUTING_BY_ID(id), {
      method: 'DELETE',
    });
  }

  /**
   * Toggle inbound routing rule enabled status
   */
  async toggleInboundRoutingRule(id: string): Promise<InboundRoutingRule> {
    const response = await this.request<{ data: InboundRoutingRule }>(
      API_ENDPOINTS.INBOUND_ROUTING_TOGGLE(id),
      {
        method: 'POST',
      }
    );
    return response.data;
  }

  /**
   * Reorder inbound routing rules (globally within org)
   */
  async reorderInboundRoutingRules(
    data: ReorderInboundRoutingRulesRequest
  ): Promise<InboundRoutingReorderResponse> {
    return this.request<InboundRoutingReorderResponse>(
      API_ENDPOINTS.INBOUND_ROUTING_REORDER,
      {
        method: 'POST',
        body: JSON.stringify(data),
        expectedRefusals: REORDER_CONFLICT_REFUSALS,
      }
    );
  }

  /**
   * Get organization fallback settings
   */
  async getFallbackSettings(): Promise<InboundRoutingFallbackSettings> {
    // Validated: the routing screens and the pipeline status decide "records
    // are rejected" from `defaultProjectHidden` (#2870).
    const response = await this.validatedRequest(
      API_ENDPOINTS.INBOUND_ROUTING_FALLBACK,
      inboundRoutingFallbackResponseSchema
    );
    return response.data;
  }

  /**
   * Update organization fallback settings
   */
  async updateFallbackSettings(
    data: UpdateInboundRoutingFallbackRequest
  ): Promise<InboundRoutingFallbackSettings> {
    const response = await this.request<InboundRoutingFallbackResponse>(
      API_ENDPOINTS.INBOUND_ROUTING_FALLBACK,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      }
    );
    return response.data;
  }
}
